import React, { useRef, useMemo, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { TEX } from "../lib/xrstore";
import { textures } from "../lib/textures";
import { SUN_POS } from "../lib/eclipse";

const vertex = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vNW;
  void main() {
    vUv = uv;
    vNW = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const fragment = /* glsl */ `
  uniform sampler2D uDay;
  uniform sampler2D uNight;
  uniform float uHasTex;
  uniform vec3 uSunDir;
  uniform float uEclipse;
  varying vec2 vUv;
  varying vec3 vNW;
  void main() {
    float ndl = dot(normalize(vNW), normalize(uSunDir));
    float dayAmt = smoothstep(-0.12, 0.22, ndl);
    vec3 day, night;
    if (uHasTex > 0.5) {
      day = texture2D(uDay, vUv).rgb;
      night = texture2D(uNight, vUv).rgb * 2.9; // vivid city lights
    } else {
      day = vec3(0.12, 0.32, 0.55);
      night = vec3(0.02, 0.03, 0.05);
    }
    // Day side +40% brighter so continents/coastlines pop; night keeps a low floor so lights shine.
    vec3 dayLit = day * (1.9 + 1.2 * dayAmt);
    vec3 col = mix(max(night, vec3(0.02)), dayLit, dayAmt);
    // soft twilight copper band along the terminator (kept subtle)
    float term = smoothstep(0.5, 0.0, abs(ndl));
    col += vec3(0.35, 0.15, 0.06) * term * 0.16;
    // ECLIPSE: only a faint red tint — the Earth stays bright & fully readable at all times.
    vec3 dimmed = col * vec3(1.08, 0.72, 0.6);
    vec3 redGlow = vec3(0.22, 0.05, 0.02) * (0.3 + dayAmt * 0.5);
    col = mix(col, dimmed + redGlow, clamp(uEclipse, 0.0, 1.0) * 0.32);
    gl_FragColor = vec4(col, 1.0);
  }
`;

export default function EarthBody({ position = [0, 0, 0], controlsRef, simRef, onEarthClick }) {
  const grp = useRef();
  const redRim = useRef();
  const vel = useRef(0);
  const dragging = useRef(false);
  const lastX = useRef(0);
  const lastDx = useRef(0);
  const moved = useRef(0);
  const matRef = useRef();
  const { gl } = useThree();

  const uniforms = useMemo(
    () => ({
      uDay: { value: textures.earthDay },
      uNight: { value: textures.earthNight },
      uHasTex: { value: textures.earthDay ? 1 : 0 },
      uSunDir: { value: SUN_POS.clone().normalize() },
      uEclipse: { value: 0 },
    }),
    []
  );

  useEffect(() => {
    if (textures.earthDay) { uniforms.uDay.value = textures.earthDay; uniforms.uNight.value = textures.earthNight; uniforms.uHasTex.value = 1; }
  }, [uniforms]);

  useEffect(() => {
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      vel.current = lastDx.current * 4;
      if (controlsRef?.current) controlsRef.current.enabled = true;
      gl.domElement.style.cursor = "grab";
    };
    const move = (e) => {
      if (!dragging.current) return;
      const dx = (e.clientX - lastX.current) * 0.01;
      if (grp.current) grp.current.rotation.y += dx;
      lastDx.current = dx;
      moved.current += Math.abs(dx);
      lastX.current = e.clientX;
    };
    window.addEventListener("pointerup", up);
    window.addEventListener("pointermove", move);
    return () => { window.removeEventListener("pointerup", up); window.removeEventListener("pointermove", move); };
  }, [gl, controlsRef]);

  useFrame((_, dt) => {
    if (!grp.current) return;
    const base = 0.12; // Earth rotates ~once per "24h" (one turn ~52s here)
    if (!dragging.current) {
      grp.current.rotation.y += (base + vel.current) * dt;
      vel.current *= Math.pow(0.86, dt * 60); // friction -> auto-spin resumes
    }
    // Smoothly ease the eclipse lighting factor from the moon's orbital position (coverage).
    if (matRef.current && simRef?.current) {
      const target = Math.min(1, Math.max(0, simRef.current.coverage));
      const cur = matRef.current.uniforms.uEclipse.value;
      matRef.current.uniforms.uEclipse.value = cur + (target - cur) * Math.min(1, dt * 3.5);
    }
    // Red atmospheric rim glow reflecting refracted light during the eclipse
    if (redRim.current && simRef?.current) {
      const cov = Math.min(1, Math.max(0, simRef.current.coverage));
      const base2 = [0.28, 0.15, 0.06];
      redRim.current.children.forEach((m, i) => { m.material.opacity = (base2[i] ?? 0) * cov; });
    }
  });

  const onDown = (e) => {
    e.stopPropagation();
    dragging.current = true;
    lastX.current = e.clientX;
    lastDx.current = 0;
    moved.current = 0;
    if (controlsRef?.current) controlsRef.current.enabled = false;
    gl.domElement.style.cursor = "grabbing";
  };

  const onClick = (e) => {
    e.stopPropagation();
    if (moved.current < 0.12 && onEarthClick) onEarthClick(e.point);
  };

  return (
    <group position={position} rotation={[0, 0, 0.4102]}>
      <group ref={grp} onPointerDown={onDown} onClick={onClick}>
        <mesh>
          <sphereGeometry args={[7.0, 96, 96]} />
          <shaderMaterial ref={matRef} vertexShader={vertex} fragmentShader={fragment} uniforms={uniforms} />
        </mesh>
      </group>
      {/* Rayleigh atmosphere glow — brighter shells so the globe pops against space */}
      <mesh scale={1.045}>
        <sphereGeometry args={[7.0, 64, 64]} />
        <meshBasicMaterial color={"#5aa0ff"} transparent opacity={0.28} side={THREE.BackSide} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      <mesh scale={1.11}>
        <sphereGeometry args={[7.0, 48, 48]} />
        <meshBasicMaterial color={"#2f74d0"} transparent opacity={0.14} side={THREE.BackSide} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      <mesh scale={1.19}>
        <sphereGeometry args={[7.0, 40, 40]} />
        <meshBasicMaterial color={"#1e5fb0"} transparent opacity={0.07} side={THREE.BackSide} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      {/* Red refracted-light rim glow — feathered nested shells, intensifies during the eclipse */}
      <group ref={redRim}>
        <mesh scale={1.035}>
          <sphereGeometry args={[7.0, 64, 64]} />
          <meshBasicMaterial color={"#ff4a24"} transparent opacity={0} side={THREE.BackSide} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        <mesh scale={1.1}>
          <sphereGeometry args={[7.0, 48, 48]} />
          <meshBasicMaterial color={"#ff5a2a"} transparent opacity={0} side={THREE.BackSide} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        <mesh scale={1.22}>
          <sphereGeometry args={[7.0, 40, 40]} />
          <meshBasicMaterial color={"#ff6a3d"} transparent opacity={0} side={THREE.BackSide} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
      </group>
    </group>
  );
}
